/**
 * E2E renderer test probe — injects a synthetic encoder PostHog envelope through
 * the REAL renderer forwarding path (`captureEncoderPostHogEnvelope` →
 * PostHogManager.capture), exposed to Playwright via
 * `globalThis.__irisEncoderErrorProbe`.
 *
 * ONE probe serves every encoder send-boundary receipt. Whether an injection is
 * crash-class is a property of the ENVELOPE the caller passes (`severity`), not
 * of the probe, so a crash-specific twin would be this same module under a second
 * name — two bundled copies, two globals, and two places to re-learn the gate
 * lesson below. Per `think-in-systems` (ai.knowledge/global) and the sibling
 * convention (device-probe.ts, calibration-probe.ts): one probe per subsystem,
 * named for the module it drives (posthog/encoder-errors.ts).
 *
 * WHY a probe rather than driving a real crash: the fatal-leveling, process_type
 * projection and rename dual-emit under test all live entirely in renderer TS
 * (encoder-errors.ts), but the real trigger is a Newts message the Go encoder
 * publishes over LiveKit — a hardware/bridge path a plain Electron e2e cannot
 * reach without a live agent. This probe hands the same envelope straight to the
 * function proxy.ts calls on a real encoder Newts message, so the receipts assert
 * the genuine projection with no Go process, agent link, or camera. NOT a HIL probe.
 *
 * Per `stay-in-scope` (ai.knowledge/global) the surface is exactly what the
 * receipts need — one inject method — and nothing more.
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
import {
  captureEncoderPostHogEnvelope,
  type EncoderPostHogEnvelope,
  type StudioEncoderErrorContext
} from '#~/posthog/encoder-errors.js';

interface EncoderErrorProbe {
  /**
   * Route a synthetic envelope through the default PostHogManager instance — the
   * exact path a real encoder Newts message takes at proxy.ts. Returns the capture
   * result so a receipt can fail fast when validation rejected the envelope
   * instead of waiting out its poll on an event that was never emitted.
   *
   * `context` is optional: a receipt indifferent to attribution gets a stable
   * synthetic agent id, and one that pins its own overrides any field.
   */
  inject(envelope: EncoderPostHogEnvelope, context?: Partial<StudioEncoderErrorContext>): boolean;
}

// Runtime gate via the preload bridge at `window.electron.process.env` — the same
// read `posthog/manager.ts`'s readE2EFeatureFlagOverrides and auto-link-guard.tsx
// use, and the ONLY one that reflects the e2e fixture's launch-time IRIS_E2E_TEST
// in the plain (non-HIL) renderer build.
//
// NOT bare `process.env.IRIS_E2E_TEST` (the form device-probe.ts uses). That is
// undefined in the packaged app at the moment this module initialises, so the probe
// silently never installs and the receipts that depend on it can never run.
// calibration-probe.ts hit this exact bug and was moved off the bare read during the
// ENG-4281 CI investigation (2026-07-23); a later single-renderer diagnostic
// reproduced it, with the bridge-gated calibration probe installing while both
// bare-gated probes did not.
//
// try/catch retained so a missing bridge (e.g. a worker context) no-ops instead of
// crashing renderer init (device-probe.ts precedent).
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

if (isE2eEnabled()) {
  const probe: EncoderErrorProbe = {
    inject(
      envelope: EncoderPostHogEnvelope,
      context: Partial<StudioEncoderErrorContext> = {}
    ): boolean {
      // A stable synthetic agent id keeps the injected event's insert-id and groups
      // deterministic across a receipt's polls; callers may override any field.
      const resolvedContext: StudioEncoderErrorContext = {
        agentId: 'e2e-encoder-probe-agent',
        ...context
      };
      return captureEncoderPostHogEnvelope(envelope, resolvedContext);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisEncoderErrorProbe = probe;
}
